import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Image as ImageIcon, X } from 'lucide-react';
import BaseForm from './BaseForm';

const NewsForm = ({
  formData,
  setFormData,
  onSubmit,
  onCancel,
  submitting,
  isEdit = false
}) => {
  const [previewUrl, setPreviewUrl] = useState(() => {
    return formData.imageUrl || '';
  });

  useEffect(() => {
    if (!formData.imageUrl) {
      setPreviewUrl('');
      return;
    }

    if (formData.imageUrl.startsWith('data:')) {
      setPreviewUrl(formData.imageUrl);
      return;
    }

    setPreviewUrl(formData.imageUrl);
  }, [formData.imageUrl]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        e.target.value = '';
        return;
      }

      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast.error('Image size should be less than 5MB');
        e.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
        setFormData(prev => ({
          ...prev,
          imageFile: file,
          imageUrl: reader.result
        }));
      };
      reader.onerror = () => {
        console.error('Error reading file');
        toast.error('Error reading file. Please try another image.');
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setPreviewUrl('');
    setFormData(prev => ({
      ...prev,
      imageFile: null,
      imageUrl: ''
    }));
    const fileInput = document.getElementById('imageFile');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const validateForm = () => {
    if (!formData.title?.trim()) {
      toast.error('Title is required');
      return false;
    }
    if (!formData.content || formData.content.length < 10) {
      toast.error('Content must be at least 10 characters long');
      return false;
    }
    if (!formData.category) {
      toast.error('Please select a category');
      return false;
    }
    if (!formData.date) {
      toast.error('Date is required');
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(e);
    }
  };

  return (
    <BaseForm
      title={isEdit ? 'Edit News' : 'Add News'}
      description="Add or update news article details"
      onSubmit={handleSubmit}
      onCancel={onCancel}
      submitting={submitting}
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="title" className="block text-sm font-medium mb-1">Title *</label>
          <Input
            id="title"
            value={formData.title || ''}
            onChange={handleChange}
            placeholder="News title"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="category" className="block text-sm font-medium mb-1">Category *</label>
            <Select
              value={formData.category || ''}
              onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Announcement">Announcement</SelectItem>
                <SelectItem value="MoU">MoU</SelectItem>
                <SelectItem value="Achievement">Achievement</SelectItem>
                <SelectItem value="Press Release">Press Release</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label htmlFor="date" className="block text-sm font-medium mb-1">Date *</label>
            <Input
              id="date"
              type="date"
              value={formData.date || ''}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
          <div>
            <p className="text-sm font-medium">Featured on Home Page</p>
            <p className="text-xs text-slate-500">Featured news appears on the home page carousel/section.</p>
          </div>
          <Switch
            checked={!!formData.isFeatured}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isFeatured: !!checked }))}
            disabled={submitting}
          />
        </div>

        <div>
          <label htmlFor="content" className="block text-sm font-medium mb-1">Content *</label>
          <Textarea
            id="content"
            value={formData.content || ''}
            onChange={handleChange}
            placeholder="Full content of the news article"
            rows={6}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">News Image</label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md">
            <div className="space-y-1 text-center">
              {previewUrl ? (
                <div className="relative">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="max-h-64 mx-auto rounded-md"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 text-white hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <div className="mt-2 text-xs text-green-600">Image loaded - Click X to remove</div>
                </div>
              ) : (
                <>
                  <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="imageFile"
                      className="relative cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500 px-3 py-2 border border-indigo-600 rounded-md hover:bg-indigo-50"
                    >
                      <span>Upload a file</span>
                      <input
                        id="imageFile"
                        name="imageFile"
                        type="file"
                        className="sr-only"
                        onChange={handleFileChange}
                        accept="image/*"
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                  {isEdit && !previewUrl && !formData.imageUrl && (
                    <div className="mt-2 text-xs text-orange-600 font-semibold">No image - upload one to add</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </BaseForm>
  );
};

export default NewsForm;